<template>
  <search-provider
    :query="query"
    :placename="placename"
    :data-lang="lang"
    :selected-category-ids="categoryIds"
  />
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useNuxtApp, createError } from 'nuxt/app'
import { httpHeadersCache } from '~/apps/search/helpers'
import { isSearchUrlRoute } from '~/apps/search/isSearchUrlComponent'
import { ensureArray } from '~/utility/array'
import CATEGORY_MAP from '../../json/catRoot.json'
import { getFeedback } from '@sentry/nuxt'
const { $routerClient, $t } = useNuxtApp()

// Page metadata
definePageMeta({
  key: 'SearchPage',
  // Removed layout: false to use default layout at build time instead of runtime
  validate: (route) => isSearchUrlRoute(route),
  httpHeaders: (to) => httpHeadersCache(to),
})

// Validation and error handling
const route = useRoute()
const { $gtm } = useNuxtApp()

if (!isSearchUrlRoute(route)) {
  throw createError({
    statusCode: 404,
    statusMessage: `Page Not Found: ${route.path}`,
    fatal: process.client,
  })
}

// Computed properties
const lang = computed(() => route.params.lang || 'en-gb')

const categoryIds = computed(() => ensureArray(route.query.type) as string[])

const categorySlugs = computed(
  () =>
    categoryIds.value
      ?.map((catId: string) => CATEGORY_MAP[catId])
      .filter((slug) => slug) || [],
)

const query = computed(
  () => (ensureArray(route.query.q)[0] as string) || undefined,
)

const placename = computed(
  () => (ensureArray(route.query.placename)[0] as string) || undefined,
)

// Watchers
watch(
  categoryIds,
  () => {
    $gtm?.push({
      pageCategories: categorySlugs.value.join(','),
    })
  },
  { immediate: true },
)

// Sentry widget for AI search referrals
let widget: any = undefined

onMounted(() => {
  // Check if user came from AI search page
  const referrer = $routerClient?.getReferrer()
  const isFromAiSearch = referrer?.fullPath.includes('/ai/')

  if (isFromAiSearch) {
    const feedback = getFeedback()
    widget = feedback?.createWidget({
      triggerLabel: $t('Send Feedback'),
      triggerAriaLabel: $t('Send feedback'),
      formTitle: $t('Send us your feedback'),
      messagePlaceholder: $t('How did we do?'),
      submitButtonLabel: $t('Send Feedback'),
      successMessageText: $t('Thank you for your feedback!'),
    })
  }
})

onUnmounted(() => {
  widget?.removeFromDom()
})
</script>
